import type { WalletAmounts } from "./wire.ts";

/**
 * The page's wallet (P2.8-T38, ADR-124 decisions 4 and 6), owned by the game rather than by React, as the bag's store
 * is. It holds only what the server last said: each `wallet` replaces it whole, and until the first arrives it holds
 * nothing, so the wallet line draws nothing rather than a wrong 0 (decision 4.3). A session's end forgets it, so the
 * next character's line waits for that character's `wallet`. Listeners are told only on a change, and the snapshot is
 * replaced on each change and kept otherwise, as `useSyncExternalStore` needs.
 *
 * @example
 * ```ts
 * const wallet = new WalletStore();
 * wallet.receive({ coin: 34512 });
 * wallet.getSnapshot();
 * // { coin: 34512 }
 * ```
 */

/**
 * What the server last said the character holds, or null before a `wallet` has arrived.
 */
export type WalletSnapshot = WalletAmounts | null;

/**
 * What the wallet line reads: a snapshot and a way to hear it change.
 */
export interface WalletSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => WalletSnapshot;
}

/**
 * The store.
 */
export class WalletStore implements WalletSource {
  private readonly listeners = new Set<() => void>();
  private snapshot: WalletSnapshot = null;

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the line reads, the same object until something changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): WalletSnapshot => this.snapshot;

  /**
   * Takes a `wallet`: the amounts replace what the store held.
   *
   * @param amounts Each currency the character holds, by its code.
   */
  public receive(amounts: WalletAmounts): void {
    this.change(amounts);
  }

  /**
   * Forgets the wallet as a session ends: the next `welcome` is followed by a `wallet`, and until then the page knows
   * nothing of it.
   */
  public reset(): void {
    if (this.snapshot !== null) {
      this.change(null);
    }
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The new snapshot.
   */
  private change(next: WalletSnapshot): void {
    this.snapshot = next;
    for (const listener of [...this.listeners]) {
      listener();
    }
  }
}
